import {
  Component,
  DestroyRef,
  inject,
  signal,
  TemplateRef,
  viewChild,
  ChangeDetectionStrategy,
  input,
} from "@angular/core";
import { takeUntilDestroyed, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { DatePipe } from "@angular/common";
import { AmcAnalysis, AmcAnalysisRequest, AmcAnalysisStatus } from "@front/m19-api-client";
import { AccountSelectionService, AuthService, StrategyCache } from "@front/m19-services";
import {
  IAlertComponent,
  IBadgeComponent,
  IBadgeVariants,
  IButtonDirective,
  IDataTableComponent,
  IIconComponent,
  ITableDataSource,
  ITableImports,
  ITableTextFilterConfig,
  ModalService,
  SpinnerComponent,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { AmcStrategyAnalysisService } from "@m19-board/amc/amc-strategy-analysis-service/amc-strategy-analysis.service";
import { StrategyLinkComponent } from "@m19-board/shared/strategy-link/strategy-link.component";
import { RouterLink } from "@angular/router";
import { combineLatest, combineLatestWith, filter, finalize, map, switchMap } from "rxjs";

const STATUS_COLORS: Record<AmcAnalysisStatus, IBadgeVariants["color"]> = {
  [AmcAnalysisStatus.PENDING]: "gray",
  [AmcAnalysisStatus.RUNNING]: "blue",
  [AmcAnalysisStatus.READY]: "green",
  [AmcAnalysisStatus.FAILED]: "red",
};

type AmcAnalysisRow = AmcAnalysis & { strategyName: string };

@Component({
  selector: "app-amc-strategy-analyses",
  imports: [
    DatePipe,
    TranslocoDirective,
    ITableImports,
    IDataTableComponent,
    IAlertComponent,
    IBadgeComponent,
    IButtonDirective,
    IIconComponent,
    SpinnerComponent,
    StrategyLinkComponent,
    RouterLink,
  ],
  templateUrl: "./amc-strategy-analyses.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AmcStrategyAnalysesComponent {
  private readonly amcStrategyAnalysisService = inject(AmcStrategyAnalysisService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly modalService = inject(ModalService);
  private readonly translocoService = inject(TranslocoService);
  private readonly strategyCache = inject(StrategyCache);

  strategyId = input<number>();

  private readonly templatesModal = viewChild.required<TemplateRef<unknown>>("templatesModal");
  readonly templatesModalRequests = signal<AmcAnalysisRequest[]>([]);

  readonly AmcAnalysisStatus = AmcAnalysisStatus;
  readonly STATUS_COLORS = STATUS_COLORS;

  readonly hasDspAdvertiser = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(map((am) => !!am.dspAdvertiserId)),
  );
  readonly readOnly = toSignal(this.accountSelectionService.readOnlyMode$, { initialValue: true });
  readonly analyses = toSignal(this.amcStrategyAnalysisService.analyses$);
  readonly strategyId$ = toObservable(this.strategyId);
  private readonly analyses$ = this.amcStrategyAnalysisService.analyses$.pipe(
    filter((analyses): analyses is AmcAnalysis[] => analyses !== undefined),
    combineLatestWith(this.strategyId$),
    map(([analyses, strategyId]) =>
      strategyId === undefined ? analyses : analyses.filter((a) => a.strategyId === strategyId),
    ),
  );
  private readonly rows$ = combineLatest([
    this.analyses$,
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.strategyCache.getAllStrategyIndex(am.accountId, am.marketplace)),
    ),
  ]).pipe(
    map(([analyses, strategies]) =>
      analyses.map((analysis) => ({
        ...analysis,
        strategyName: analysis.strategyIds.map((strategyId) => strategies.get(strategyId)?.name ?? "").join(", "),
      })),
    ),
  );

  readonly dataSource = new ITableDataSource<AmcAnalysisRow>(
    this.rows$,
    undefined,
    undefined,
    new ITableTextFilterConfig<AmcAnalysisRow>((row, filterValue) => {
      const search = filterValue.toLowerCase();
      return row.strategyName.toLowerCase().includes(search);
    }),
  );
  readonly downloadingRequestIds = signal<ReadonlySet<number>>(new Set());

  readonly locale = toSignal(this.authService.userLocale$, { requireSync: true });

  openTemplatesModal(analysis: AmcAnalysis) {
    this.templatesModalRequests.set(analysis.requests);
    this.modalService.openModal(this.templatesModal(), {
      modalTitle: this.translocoService.translate("amc.strategy_analysis_templates"),
    });
  }

  download(analysis: AmcAnalysis) {
    const requestId = analysis.analysisId;
    this.downloadingRequestIds.update((ids) => new Set(ids).add(requestId));
    this.amcStrategyAnalysisService
      .download(analysis)
      .pipe(
        finalize(() =>
          this.downloadingRequestIds.update((ids) => {
            const remaining = new Set(ids);
            remaining.delete(requestId);
            return remaining;
          }),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({ error: () => {} });
  }
}
